import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineEnvelopeAltIcon from 'LuxKit/Icons/line/LineEnvelopeAltIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import zIndex from 'styles/tools/z-index'
import styled from 'styled-components'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'

const StyledModalContent = styled(ModalContent)`
  padding-bottom: ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(24)};
  }
`

const ModalContentWrapper = styled(Group)`
  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(16)};
  }
`

const ConciergeDetailsCard = styled(Sheet)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(12)};
  text-align: center;
`

const ConciergeContactDetails = styled(Group)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const ConciergePhoneLink = styled(TextLink)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
`

const BenefitsList = styled(Group)`
  width: 100%;
  margin-bottom: ${rem(20)};

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(16)};
`

const BenefitCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const MobileContactFooter = styled.footer`
  display: flex;
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.top.small};
  z-index: ${zIndex.min};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const MobileContactActions = styled.div`
  display: flex;
  gap: ${rem(8)};
  width: 100%;

  > * {
    flex: 1 1 0;
  }
`

function InfiniteConciergeDetailsModal() {
  const intl = useIntl()
  const conciergeDetails = useInfiniteConciergeDetails()

  const benefits = [
    {
      title: intl.formatMessage({
        id: 'n5cBV2dlGq2zJ0XdiLGI',
        defaultMessage: 'Support available 24/7',
        description: 'Benefit title in the Infinite travel concierge details modal',
      }),
      description: intl.formatMessage({
        id: '7Vuoj_nQjZB5QinZrIjL',
        defaultMessage: 'One call from wherever you are.',
        description: 'Benefit description in the Infinite travel concierge details modal',
      }),
    },
    {
      title: intl.formatMessage({
        id: 'FNQx6ssdSmu9ul8qSff-',
        defaultMessage: 'Personal by design',
        description: 'Benefit title in the Infinite travel concierge details modal',
      }),
      description: intl.formatMessage({
        id: 'nZShJ6QvIeyQmW_9JyzB',
        defaultMessage: 'Recommendations and arrangements shaped around your preferences.',
        description: 'Benefit description in the Infinite travel concierge details modal',
      }),
    },
    {
      title: intl.formatMessage({
        id: 'v_iBZ6_CCWGdWm533v2v',
        defaultMessage: 'Every detail handled',
        description: 'Benefit title in the Infinite travel concierge details modal',
      }),
      description: intl.formatMessage({
        id: 'bTEH-gRxWTktPzfkBgrb',
        defaultMessage: 'Special requests, itinerary changes, the details that matter. All confirmed.',
        description: 'Benefit description in the Infinite travel concierge details modal',
      }),
    },
  ]

  const title = intl.formatMessage({
    id: '_BZFPyTRFi4non-fXQj5',
    defaultMessage: 'Your dedicated Travel Concierge',
    description: 'Title for the Infinite travel concierge details modal',
  })

  return <ModalBase size="S">
    <ModalHeader title={title} />
    <ModalBody>
      <StyledModalContent>
        <ModalContentWrapper direction="vertical" gap={20}>
          <ConciergeDetailsCard
            background="light-background"
            borderRadius="L"
            padding="24 20"
          >
            <Heading variant="heading5" as="h3">{conciergeDetails.name}</Heading>
            <ConciergeContactDetails direction="vertical" gap={8} horizontalAlign="center">
              <ConciergePhoneLink
                href={conciergeDetails.phoneHref}
                size="large"
                weight="regular"
                underline={false}
                startIcon={<LinePhoneIcon />}
              >
                {conciergeDetails.phoneNumber}
              </ConciergePhoneLink>
              <BodyTextBlock
                variant="large"
                startIcon={<LineEnvelopeAltIcon />}
                endIcon={<CopyToClipboardClickableIcon
                  value={conciergeDetails.email}
                  ariaLabel={intl.formatMessage({
                    id: 'BQowl7S21lXtppEw2NrB',
                    defaultMessage: 'Copy concierge email address',
                    description: 'Aria label for the Infinite concierge email copy button',
                  })}
                />}
                align="center"
              >
                {conciergeDetails.email}
              </BodyTextBlock>
            </ConciergeContactDetails>
          </ConciergeDetailsCard>

          <BenefitsList direction="vertical" gap={16}>
            {benefits.map(benefit => <BenefitItem key={benefit.title}>
              <LineCheckIcon size="L" />
              <BenefitCopy>
                <BodyText variant="medium" weight="bold">{benefit.title}</BodyText>
                <BodyText variant="medium">{benefit.description}</BodyText>
              </BenefitCopy>
            </BenefitItem>)}
          </BenefitsList>
        </ModalContentWrapper>
      </StyledModalContent>
    </ModalBody>
    <MobileContactFooter>
      <MobileContactActions>
        <TextButton
          kind="secondary"
          size="large"
          href={conciergeDetails.emailHref}
          startIcon={<LineEnvelopeAltIcon />}
          fit="flex"
        >
          {intl.formatMessage({
            id: 'z5rVH_0Vt7sD_7_DeCAm',
            defaultMessage: 'Email',
            description: 'Email action label for Infinite concierge in account drawer',
          })}
        </TextButton>
        <TextButton
          kind="secondary"
          size="large"
          href={conciergeDetails.phoneHref}
          startIcon={<LinePhoneIcon />}
          fit="flex"
        >
          {intl.formatMessage({
            id: '6gAJalaK7gPvSKsgR7PW',
            defaultMessage: 'Call',
            description: 'Call action label for Infinite concierge in account drawer',
          })}
        </TextButton>
      </MobileContactActions>
    </MobileContactFooter>
  </ModalBase>
}

export default InfiniteConciergeDetailsModal
